import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoggingService } from '@logging/logging.service';
import { OnscreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';
import { HolidayDataUpdatedService } from '@shared-update-services';
import { AbstractEntityState } from '@entity-framework/entity-record/state/abstract-entity.state';
import { AbstractEntityStateService } from '@entity-framework/entity-record/state/abstract-entity.state-service';
import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';
import { HolidayApproverSummaryDataProvider } from './holiday-approver-summary.data-provider';
import { HolidayApproverSummary } from './holiday-approver-summary';

/**
 * Default empty holiday approver summary
 */
export const emptyHolidayApproverSummary: HolidayApproverSummary = {
  isHolidayApprover: false,
  isHolidaySelfApprover: false,
  openHolidayRequestCount: 0,
  openAbsenceRequestCount: 0,
  id: 0
};

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class HolidayApproverSummaryStateService
  extends AbstractEntityStateService<HolidayApproverSummary, AbstractEntityState<HolidayApproverSummary>>
  implements OnDestroy
{
  constructor(
    protected dataProvider: HolidayApproverSummaryDataProvider,
    protected route: ActivatedRoute,
    protected messaging: OnscreenMessagingService,
    protected logger: LoggingService,
    private holidayDataUpdatedService: HolidayDataUpdatedService,
    private activeSecurity: ActiveSecurityContextStateService
  ) {
    super('HolidayApproverSummaryState', dataProvider, route, messaging, logger);
    this.holidayDataUpdatedService.updated.subscribe(this.fetchEntity);
    this.initializeHolidayApproverSummary();
  }

  private initializeHolidayApproverSummary() {
    this.activeSecurity.activeMembership$
      .pipe(
        untilDestroyed(this),
        filter((m) => !!m),
        map((m) => m.organisationId),
        distinctUntilChanged()
      )
      .subscribe((m) => {
        if (this.activeSecurity.canAccessHolidays()) {
          this.fetchEntity();
        } else {
          this.entity = emptyHolidayApproverSummary;
        }
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
